import { useEffect, useRef, useState, type FormEvent, type KeyboardEvent } from 'react';
import { useSearchParams } from 'react-router';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CHAT_LIMITS, CHAT_RATING_LABELS, CHAT_STATUS_LABELS, type ChatDetail, type ChatOverview, type ChatSummary, type Paginated } from '@kontor/shared';
import { api, queryString } from '../../api';
import { FilterBar, type FilterField } from '../../components/filter/FilterBar';
import { useSession } from '../../session';
import { RatingsPanel, Stars } from './RatingsPanel';
import './chat.css';

const POLL_MS = 4000;
/** Die offene Unterhaltung fragt öfter nach, damit „schreibt …“ rechtzeitig erscheint */
const DETAIL_POLL_MS = 2500;
const time = new Intl.DateTimeFormat('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Berlin' });
const clock = new Intl.DateTimeFormat('de-DE', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Berlin' });
const who = (c: Pick<ChatSummary, 'visitorName' | 'visitorEmail'>) => c.visitorName ?? c.visitorEmail ?? 'Besucher';

type Filter = 'waiting' | 'open' | 'closed' | 'mine';
const FILTERS: { key: Filter; label: string }[] = [
  { key: 'waiting', label: 'Warten auf Antwort' },
  { key: 'open', label: 'Offen' },
  { key: 'mine', label: 'Meine' },
  { key: 'closed', label: 'Geschlossen' },
];
const PER_PAGE = 30;
/** Weitere Filter neben den Reitern, in der Adresszeile gemerkt: Suche, Zeitraum der letzten Nachricht, Bewertung, Kanal, Betreuung */
const EXTRA_KEYS = ['q', 'von', 'bis', 'bew', 'kanal', 'zust'] as const;
const FIELDS: FilterField[] = [
  { key: 'von', label: 'Letzte Nachricht ab', type: 'date' },
  { key: 'bis', label: 'Letzte Nachricht bis', type: 'date' },
  {
    key: 'bew',
    label: 'Bewertung',
    type: 'select',
    options: [{ value: '', label: 'Alle' }, ...[5, 4, 3, 2, 1].map((n) => ({ value: String(n), label: `${n} ${n === 1 ? 'Stern' : 'Sterne'} (${CHAT_RATING_LABELS[n]})` })), { value: 'none', label: 'Ohne Bewertung' }],
  },
  { key: 'kanal', label: 'Kanal', type: 'select', options: [{ value: '', label: 'Alle' }, { value: 'web', label: 'Webseite' }, { value: 'facebook', label: 'Facebook' }] },
  { key: 'zust', label: 'Betreuung', type: 'select', options: [{ value: '', label: 'Alle' }, { value: 'none', label: 'Noch niemand zugewiesen' }] },
];
const filterQuery = (f: Filter) => (f === 'waiting' ? { waiting: '1' } : f === 'open' ? { status: 'open' } : f === 'closed' ? { status: 'closed' } : { mine: '1' });

/**
 * Webchat (KON-173): links die Unterhaltungen mit Besuchern der Webseite, rechts der Verlauf mit Antwortfeld. Die Liste und der offene Verlauf aktualisieren sich alle paar Sekunden.
 * Auf dem Handy zeigt die Seite entweder die Liste oder den Verlauf.
 */
export function ChatsPage() {
  const { can } = useSession();
  const [params, setParams] = useSearchParams();
  const selected = params.get('c');
  const ratings = params.get('v') === 'bewertungen';
  const [filter, setFilter] = useState<Filter>('waiting');
  const extra = Object.fromEntries(EXTRA_KEYS.map((k) => [k, params.get(k) ?? ''])) as Record<(typeof EXTRA_KEYS)[number], string>;
  const page = Math.max(1, Number(params.get('seite')) || 1);
  const filtered = EXTRA_KEYS.some((k) => extra[k] !== '');
  // Eine Änderung der Filter springt zurück auf die erste Seite. Die geöffnete Unterhaltung bleibt.
  const setExtra = (changes: Record<string, string>) =>
    setParams((current) => {
      const next = new URLSearchParams(current);
      for (const [k, v] of Object.entries(changes)) {
        if (v) next.set(k, v);
        else next.delete(k);
      }
      if (!('seite' in changes)) next.delete('seite');
      return next;
    });
  // Zwischen Unterhaltungen und Bewertungen wechseln, ohne die gesetzten Filter zu verlieren
  const view = (v: string | null) =>
    setParams((current) => {
      const next = new URLSearchParams();
      if (v) next.set('v', v);
      else {
        if (selected) next.set('c', selected);
        for (const k of EXTRA_KEYS) if (current.get(k)) next.set(k, current.get(k)!);
      }
      return next;
    });

  const list = useQuery({
    queryKey: ['webchat', 'list', filter, extra, page],
    queryFn: async () =>
      await api<Paginated<ChatSummary>>(
        'GET',
        `/webchat/conversations${queryString({ ...filterQuery(filter), q: extra.q, from: extra.von, to: extra.bis, rating: extra.bew, channel: extra.kanal, assigned: extra.zust, page, perPage: PER_PAGE })}`,
      ),
    placeholderData: keepPreviousData,
    refetchInterval: POLL_MS,
    refetchOnWindowFocus: true,
  });
  const overview = useQuery({ queryKey: ['webchat', 'overview'], queryFn: async () => (await api<{ data: ChatOverview }>('GET', '/webchat/overview')).data, refetchInterval: POLL_MS });

  return (
    <>
      <div className="page-head">
        <h1>Live Chats</h1>
        <span className="muted">{overview.data ? `${overview.data.waiting} warten auf Antwort, ${overview.data.open} offen` : 'Besucher Ihrer Webseite'}</span>
      </div>
      <div className="chat-filters chat-views" role="tablist" aria-label="Ansicht">
        <button type="button" role="tab" aria-selected={!ratings} className={`chat-filter${!ratings ? ' is-active' : ''}`} onClick={() => view(null)}>Unterhaltungen</button>
        <button type="button" role="tab" aria-selected={ratings} className={`chat-filter${ratings ? ' is-active' : ''}`} onClick={() => view('bewertungen')}>Bewertungen</button>
      </div>
      {ratings ? (
        <RatingsPanel onOpen={(id) => setParams({ c: id })} />
      ) : (
      <div className={`chat-layout${selected ? ' has-selection' : ''}`}>
        <aside className="chat-list card" aria-label="Unterhaltungen">
          <div className="chat-filters" role="tablist" aria-label="Filter">
            {FILTERS.map((f) => (
              <button key={f.key} type="button" role="tab" aria-selected={filter === f.key} className={`chat-filter${filter === f.key ? ' is-active' : ''}`} onClick={() => setFilter(f.key)}>
                {f.label}
              </button>
            ))}
          </div>
          <FilterBar
            search={{ value: extra.q, placeholder: 'Name, E-Mail oder Text suchen', onSubmit: (v) => setExtra({ q: v }) }}
            fields={FIELDS}
            values={{ von: extra.von, bis: extra.bis, bew: extra.bew, kanal: extra.kanal, zust: extra.zust }}
            onChange={(key, value) => setExtra({ [key]: value })}
            onReset={() => setExtra(Object.fromEntries(EXTRA_KEYS.map((k) => [k, ''])))}
          />
          {list.isLoading && <p className="muted">Wird geladen …</p>}
          {list.isError && <div className="alert alert--error" role="alert">{(list.error as Error).message}</div>}
          {list.data?.data.length === 0 && <p className="muted chat-empty">{filtered ? 'Keine Unterhaltung passt zu Ihrer Suche. Ändern Sie die Filter oder wählen Sie oben einen anderen Reiter.' : filter === 'waiting' ? 'Niemand wartet auf eine Antwort.' : 'Keine Unterhaltungen.'}</p>}
          <ul className="chat-items">
            {list.data?.data.map((c) => (
              <li key={c.id}>
                <button type="button" className={`chat-item${c.id === selected ? ' is-selected' : ''}${c.waiting ? ' is-waiting' : ''}`} onClick={() => setParams({ c: c.id })}>
                  <span className="chat-item__top">
                    <strong>{who(c)}</strong>
                    {c.channel === 'facebook' && <span className="badge">Facebook</span>}
                    <span className="muted small">{time.format(new Date(c.lastMessageAt))}</span>
                  </span>
                  <span className="chat-item__text">{c.lastMessage}</span>
                  <span className="chat-item__meta">
                    {c.unread > 0 && <span className="chat-badge" aria-label={`${c.unread} ungelesen`}>{c.unread}</span>}
                    {c.status === 'closed' && <span className="badge">{c.endedBy === 'visitor' ? 'Vom Besucher beendet' : CHAT_STATUS_LABELS.closed}</span>}
                    {c.rating !== null && <Stars value={c.rating} />}
                    {c.assignedUserName && <span className="muted small">{c.assignedUserName}</span>}
                  </span>
                </button>
              </li>
            ))}
          </ul>
          {list.data && list.data.meta.lastPage > 1 && (
            <nav className="chat-pager" aria-label="Seiten der Liste">
              <button type="button" className="btn btn--secondary btn--sm" disabled={page <= 1} onClick={() => setExtra({ seite: page > 2 ? String(page - 1) : '' })}>Zurück</button>
              <span className="muted small">Seite {page} von {list.data.meta.lastPage} ({list.data.meta.total} Unterhaltungen)</span>
              <button type="button" className="btn btn--secondary btn--sm" disabled={page >= list.data.meta.lastPage} onClick={() => setExtra({ seite: String(page + 1) })}>Weiter</button>
            </nav>
          )}
        </aside>
        <section className="chat-pane card" aria-label="Verlauf">
          {selected ? <Conversation key={selected} id={selected} canWrite={can('webchat.write')} canDelete={can('webchat.delete')} onBack={() => setParams({})} /> : <p className="muted chat-empty">Wählen Sie links eine Unterhaltung.</p>}
        </section>
      </div>
      )}
    </>
  );
}

function Conversation({ id, canWrite, canDelete, onBack }: { id: string; canWrite: boolean; canDelete: boolean; onBack: () => void }) {
  const client = useQueryClient();
  const { session } = useSession();
  const me = session?.user.id;
  const [text, setText] = useState('');
  const end = useRef<HTMLDivElement>(null);
  const lastTypingPing = useRef(0);
  const key = ['webchat', 'detail', id];
  const detail = useQuery({ queryKey: key, queryFn: async () => (await api<{ data: ChatDetail }>('GET', `/webchat/conversations/${id}`)).data, refetchInterval: DETAIL_POLL_MS, refetchOnWindowFocus: true, retry: false });
  const c = detail.data;
  const refresh = () => client.invalidateQueries({ queryKey: ['webchat'] });
  const set = (d: ChatDetail) => {
    client.setQueryData(key, d);
    void refresh();
  };

  const read = useMutation({ mutationFn: async () => (await api<{ data: ChatDetail }>('POST', `/webchat/conversations/${id}/read`)).data, onSuccess: set });
  const reply = useMutation({
    mutationFn: async (body: string) => (await api<{ data: ChatDetail }>('POST', `/webchat/conversations/${id}/messages`, { body })).data,
    onSuccess: (d) => {
      setText('');
      set(d);
    },
  });
  // Dem Besucher melden, dass gerade eine Antwort entsteht, höchstens alle drei Sekunden. Ein Fehler dabei ist unwichtig.
  const onType = (value: string) => {
    setText(value);
    if (!value.trim() || Date.now() - lastTypingPing.current < 3000) return;
    lastTypingPing.current = Date.now();
    api('POST', `/webchat/conversations/${id}/typing`).catch(() => undefined);
  };
  const update = useMutation({ mutationFn: async (body: object) => (await api<{ data: ChatDetail }>('PATCH', `/webchat/conversations/${id}`, body)).data, onSuccess: set });
  const remove = useMutation({
    mutationFn: async () => api('DELETE', `/webchat/conversations/${id}`),
    onSuccess: () => {
      void refresh();
      onBack();
    },
  });

  // Ungelesene Nachrichten gelten als gelesen, sobald der Verlauf offen ist
  const unread = c?.unread ?? 0;
  useEffect(() => {
    if (unread > 0 && !read.isPending && canWrite) read.mutate();
  }, [unread]); // eslint-disable-line react-hooks/exhaustive-deps
  const count = c?.messages.length ?? 0;
  useEffect(() => {
    end.current?.scrollIntoView({ block: 'end' });
  }, [count]);

  if (detail.isError) return <div className="alert alert--error" role="alert">{(detail.error as Error).message}</div>;
  if (!c) return <p className="muted">Wird geladen …</p>;

  const send = (e?: FormEvent) => {
    e?.preventDefault();
    const body = text.trim();
    if (body && !reply.isPending) reply.mutate(body);
  };
  const onKey = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <div className="chat-conv">
      <header className="chat-conv__head">
        <button type="button" className="btn btn--secondary btn--sm chat-back" onClick={onBack}>← Liste</button>
        <div className="chat-conv__who">
          <h2>{who(c)}{c.channel === 'facebook' && <span className="badge">Facebook</span>}</h2>
          <p className="muted small">
            {c.visitorEmail && <a href={`mailto:${c.visitorEmail}`}>{c.visitorEmail}</a>}
            {c.visitorEmail && c.pageUrl && ' · '}
            {c.pageUrl && <span title={c.pageUrl}>von {c.pageUrl.replace(/^https?:\/\//, '')}</span>}
            {(c.visitorEmail || c.pageUrl) && ' · '}
            gestartet {time.format(new Date(c.createdAt))}
            {c.assignedUserName && ` · ${c.assignedUserName}`}
          </p>
        </div>
        {canWrite && (
          <div className="chat-conv__actions">
            {c.assignedUserId === me ? (
              <button type="button" className="btn btn--secondary btn--sm" disabled={update.isPending} onClick={() => update.mutate({ assignedUserId: null })}>Freigeben</button>
            ) : (
              <button type="button" className="btn btn--secondary btn--sm" disabled={update.isPending} onClick={() => update.mutate({ assignedUserId: me })}>Mir zuweisen</button>
            )}
            <button type="button" className="btn btn--secondary btn--sm" disabled={update.isPending} onClick={() => update.mutate({ status: c.status === 'open' ? 'closed' : 'open' })}>
              {c.status === 'open' ? 'Schließen' : 'Wieder öffnen'}
            </button>
            {canDelete && (
              <button type="button" className="btn btn--danger btn--sm" disabled={remove.isPending} onClick={() => window.confirm('Diese Unterhaltung mit allen Nachrichten endgültig löschen?') && remove.mutate()}>Löschen</button>
            )}
          </div>
        )}
      </header>
      {c.rating !== null && (
        <div className="chat-rating" role="note">
          <Stars value={c.rating} /> <strong>{CHAT_RATING_LABELS[c.rating]}</strong>
          {c.ratedAt && <span className="muted small"> · bewertet {time.format(new Date(c.ratedAt))}</span>}
          {c.ratingComment && <p className="chat-rating__comment">„{c.ratingComment}“</p>}
        </div>
      )}
      <div className="chat-log" role="log" aria-live="polite">
        {c.messages.map((m) => (
          <div key={m.seq} className={`chat-msg chat-msg--${m.sender}`}>
            <div className="chat-msg__bubble">{m.body}</div>
            <div className="chat-msg__meta muted small">{m.sender === 'staff' ? `${m.senderName ?? 'Team'} · ` : ''}{clock.format(new Date(m.createdAt))}</div>
          </div>
        ))}
        {c.visitorTyping && <div className="chat-typing muted small" role="status">{c.visitorName?.trim() || 'Der Besucher'} schreibt …</div>}
        <div ref={end} />
      </div>
      {c.status === 'closed' && <p className="muted small chat-closed">{c.endedBy === 'visitor' ? 'Der Besucher hat das Gespräch beendet.' : 'Geschlossen.'} Eine Antwort öffnet die Unterhaltung wieder.</p>}
      {canWrite ? (
        <form className="chat-form" onSubmit={send}>
          <label className="sr-only" htmlFor="chat-reply">Antwort</label>
          <textarea id="chat-reply" className="input" rows={2} maxLength={CHAT_LIMITS.messageChars} value={text} onChange={(e) => onType(e.target.value)} onKeyDown={onKey} placeholder="Antwort schreiben … (Enter sendet, Umschalt+Enter ist ein Zeilenumbruch)" />
          <button type="submit" className="btn btn--primary" disabled={reply.isPending || !text.trim()}>Senden</button>
        </form>
      ) : (
        <p className="muted small">Sie dürfen mitlesen, aber nicht antworten.</p>
      )}
      {(reply.error || update.error || remove.error) && <div className="alert alert--error" role="alert">{(reply.error ?? update.error ?? remove.error)!.message}</div>}
    </div>
  );
}
